<template>
  <div id="app">
    <template v-if="isDeveloper">
      <div
        style="width: 100vw; height: 100vh; display: flex; align-items: center; justify-content: center; text-align: center;"
      >
        <h1 style="font-size: 40px">На сайте ведутся технические работы</h1>
      </div>
    </template>

    <template v-else>
      <div
        class="app__preloader"
        v-if="$store.state.showPreloader || !$store.state.syncCompleted"
        style="height: 100vh; width: 100vw; position: fixed; top: 0; left: 0; z-index: 999999;"
      >
        <Loader />
      </div>
      <div :class="{ overlay: $store.state.isOpenBurgerMenu || $store.state.isOpenUserDropMenu }"></div>
      <BurgerMenu />
      <UserDropMenu />
      <SupportChat />
      <!-- Бэкенд недоступен: показываем экран обслуживания на месте, не уводя
           на роут /503. Переход менял адрес документа, который загрузил робот,
           и на индексируемой странице оказывался noindex (RBXGD-1119).
           Прямой заход на /503 сюда не попадает — там компонент рисует роутер. -->
      <ServiceUnavailable v-if="isServiceUnavailable" />
      <router-view v-else />
      <Auth />

      <div class="notification-wrapper" :class="{ show: notifications.length }">
        <ToastNotification
          v-for="notification in notifications"
          :key="`notification-${notification.id}`"
          :id="notification.id"
          :type="notification.type"
          :title="notification.title"
          :description="notification.description"
          :delay="notification.delay"
        />
      </div>
    </template>
  </div>
</template>

<script>
import '@/scss/_reset.scss'
import '@/scss/fonts/font.css'
import '@/scss/_main.scss'

import BurgerMenu from '@/components/BurgerMenu/BurgerMenu'
import UserDropMenu from '@/components/UserDropMenu/UserDropMenu'
import Loader from '@/components/LoaderBar/LoaderBar'

import SupportChat from '@/components/SupportChat/SupportChat'
import Auth from '@/components/Auth/Auth.vue'
import ToastNotification from '@/components/Toast/Toast.vue'
import ServiceUnavailable from '@/views/ServiceUnavailable/ServiceUnavailable.vue'

import { setUserInfoForChat, setDataForChat } from '@/utils/chat'
import { loadScript, onFirstInteraction } from '@/utils/loadScript'
import { jsonLdFor } from '@/seo/pages'
import {
  DEFAULT_OG_IMAGE,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_ALT
} from '@/seo/site'
import { organization, website } from '@/seo/jsonLd'

// og:locale хочет полный тег с регионом, а в i18n лежит только язык.
const OG_LOCALES = {
  ru: 'ru_RU',
  en: 'en_US'
}

const currencyMap = {
  rub: '₽',
  usd: '$',
  eur: '€'
}

const languagesMap = ['en', 'ru', 'tr', 'fr', 'br', 'pt', 'es', 'de']

// Виджет Jivo стоял в <head> и на первой же секунде тянул девять запросов плюс
// три mp3 со звуками чата, который никто не открывал, а телеметрия висела
// длинным запросом ещё 17 секунд. Первому экрану он не нужен: собственной
// кнопки поддержки в приложении нет (SupportChat отключён через v-if="false"),
// пузырь рисует сам виджет. Грузим по первому действию пользователя или в
// простое браузера (RBXGD-1101).
const JIVO_SRC = 'https://code.jivo.ru/widget/vh5YGqjNFQ'

export default {
  name: 'App',
  components: {
    ServiceUnavailable,
    ToastNotification,
    Auth,
    BurgerMenu,
    UserDropMenu,
    Loader,
    SupportChat
  },
  // Единственное место, где проставляется мета. Роут отдаёт ключи i18n
  // (router/index.js), здесь они резолвятся — поэтому теги пересобираются и на
  // смену страницы, и на смену языка. Императивных правок document.title и
  // meta[name=description] в роутере больше нет.
  //
  // vmid обязателен: без него vue-meta не заменяет одноимённый тег, а дописывает
  // второй рядом — так на сайте и жили два description, свой и от главной.
  //
  // og/twitter (RBXGD-1089) идут тем же механизмом. Живому браузеру они не
  // нужны, но скраперы, которых нет в списке UA у nginx, получают скелет
  // index.html — а его дефолты подменяются здесь на постраничные, как только
  // страница отрисовалась. Набор тегов совпадает с пререндером (seo-template.js),
  // иначе карточка зависела бы от того, кто именно пришёл по ссылке.
  metaInfo() {
    const meta = [
      { vmid: 'description', name: 'description', content: this.metaDescription },
      { vmid: 'og:locale', property: 'og:locale', content: this.ogLocale },
      { vmid: 'og:type', property: 'og:type', content: 'website' },
      { vmid: 'og:site_name', property: 'og:site_name', content: 'RbxPier.me' },
      { vmid: 'og:title', property: 'og:title', content: this.ogTitle },
      { vmid: 'og:description', property: 'og:description', content: this.metaDescription },
      { vmid: 'og:url', property: 'og:url', content: this.pageUrl },
      { vmid: 'og:image', property: 'og:image', content: this.ogImage },
      { vmid: 'og:image:width', property: 'og:image:width', content: OG_IMAGE_WIDTH },
      { vmid: 'og:image:height', property: 'og:image:height', content: OG_IMAGE_HEIGHT },
      { vmid: 'og:image:alt', property: 'og:image:alt', content: OG_IMAGE_ALT },
      { vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
      { vmid: 'twitter:title', name: 'twitter:title', content: this.ogTitle },
      { vmid: 'twitter:description', name: 'twitter:description', content: this.metaDescription },
      { vmid: 'twitter:image', name: 'twitter:image', content: this.ogImage },
      { vmid: 'twitter:image:alt', name: 'twitter:image:alt', content: OG_IMAGE_ALT }
    ]

    if (this.isNoindex) {
      meta.push({ vmid: 'robots', name: 'robots', content: 'noindex, follow' })
    }

    return {
      // Статикой в index.html стоит ru, здесь — реактивно от локали: код на два
      // языка живой, хотя переключатель сейчас и прибит к ru.
      htmlAttrs: { lang: this.$i18n.locale },
      title: this.metaTitle,
      meta,
      link: this.canonicalHref ? [{ vmid: 'canonical', rel: 'canonical', href: this.canonicalHref }] : [],
      script: this.jsonLd
    }
  },
  provide: {
    currencyMap
  },
  data() {
    return {
      isDeveloper: false
    }
  },
  async created() {
    document.onreadystatechange = () => {
      if (document.readyState === 'complete') {
        this.$store.state.showPreloader = false
      }
    }

    await Promise.all([
      this.$store.dispatch('getRbxStock'),
      this.$store.dispatch('getRates'),
      this.$store.dispatch('getMethodRates'),
      this.$store.dispatch('getPacksConfig')
    ])

    // window.enableDevMode = () => {
    //   this.isDeveloper = true
    // }

    // Убрали динамическое переключение локализации и валюты сайта
    // await this.getCountry()
  },
  mounted() {
    // Колбэк вешаем сразу, а сам виджет грузим отложенно: Jivo вызовет
    // jivo_onOpen после своей инициализации, и глобальная переменная к тому
    // моменту уже на месте независимо от того, когда приехал скрипт.
    this.setupJivoMethods()

    onFirstInteraction(() => {
      loadScript(JIVO_SRC).catch(error => console.warn('Jivo не загрузился:', error.message))
    })
  },
  computed: {
    notifications() {
      return this.$store.getters['notification/notifications'] || []
    },
    metaTitle() {
      return this.$t(this.$route.meta.titleKey || 'app.title')
    },
    metaDescription() {
      return this.$t(this.$route.meta.descriptionKey || 'app.description')
    },
    isServiceUnavailable() {
      return this.$store.state.isServiceUnavailable
    },
    isNoindex() {
      return Boolean(this.$route.meta.noindex)
    },
    // Заголовок карточки в соцсетях. У витрин он свой (в выдаче title обязан
    // нести запрос целиком, а в мессенджере такая строка читается как спам),
    // у остальных страниц совпадает с title — тогда ключа в роуте просто нет.
    // Через $te, а не через попытку $t: на отсутствующем ключе vue-i18n вернёт
    // сам ключ, и в карточку уехало бы «app.privacy.ogTitle».
    ogTitle() {
      const key = this.$route.meta.ogTitleKey

      return key && this.$te(key) ? this.$t(key) : this.metaTitle
    },
    ogLocale() {
      return OG_LOCALES[this.$i18n.locale] || OG_LOCALES.ru
    },
    // Абсолютный адрес обязателен: относительный путь скраперы не разворачивают.
    ogImage() {
      return DEFAULT_OG_IMAGE
    },
    // og:url каноничен для репоста: Facebook и ВК схлопывают шары по нему, так
    // что query сюда не попадает — как и в canonical. Но, в отличие от него,
    // тег ставится и на закрытых страницах: og индексом не управляет, а
    // карточка без адреса разворачивается на случайный URL.
    pageUrl() {
      return window.location.origin + this.$route.path
    },
    // Микроразметка. Построители общие с пререндером (src/seo/jsonLd.js), а
    // постраничные блоки — из того же реестра, откуда пререндер берёт свои:
    // иначе Google, исполняющий JS, и Яндекс, читающий статику, видели бы разную
    // разметку. Закрытые от индексации страницы обходим — размечать нечего.
    //
    // vue-meta 2.4 умеет `json` у script-тегов нативно, поэтому ни innerHTML,
    // ни отключения санитайзеров не нужно.
    //
    // Product тут намеренно нет: цены живут в сторе и в компонентах витрин, и
    // собирать их здесь значило бы завести второй источник цен рядом с
    // генератором. Боту money-страницы отдаёт пререндер — там Product есть.
    jsonLd() {
      if (this.isNoindex) return []

      const blocks = [organization(), website(), ...jsonLdFor(this.$route.path)]

      return blocks.map((json, index) => ({
        vmid: `ld-${index}`,
        type: 'application/ld+json',
        json
      }))
    },
    // Закрытые от индексации страницы остаются без canonical: ссылка на главную
    // противоречит noindex, и Яндекс из-за неё держит такие адреса в группах
    // дублей вместо того, чтобы выбросить (наступали на это на Дереве).
    // Остальные страницы каноникалят сами на себя — query в путь не попадает.
    canonicalHref() {
      return this.isNoindex ? '' : window.location.origin + this.$route.path
    }
  },
  methods: {
    async getCountry() {
      let language = localStorage.getItem('language')
      let currency = localStorage.getItem('currency')

      fetch('https://users.backend-pier.com/current-country')
        .then(res => res.json())
        .then(data => {
          if (!language) {
            language = data.country.toLowerCase()

            if (!languagesMap.includes(language)) language = 'en'
            if (language === 'pt') language = 'br'
          }

          if (!currency) {
            const allowedCurrencies = {
              en: 'eur',
              ru: 'rub'
            }
            currency = allowedCurrencies[language] || 'usd'
          }

          this.$store.commit('setLanguage', language)
          this.$store.commit('setCurrency', currency)
          this.$i18n.locale = language
          this.$store.state.ipCountry = language
        })
        .catch(err => {
          console.log(err)
        })
    },
    onJivoOpen() {
      const username = this.$store.state.account.username
      const userId = this.$store.state.account.userId
      const userIdMask = userId?.slice(-5)

      const isPrivateUserName = username.toLowerCase()?.includes('telegram')
      const name = isPrivateUserName ? `${username} ${userIdMask}` : username

      setUserInfoForChat({ name })

      setDataForChat({
        title: 'User ID',
        content: userId
      })
    },
    setupJivoMethods() {
      window.jivo_onOpen = this.onJivoOpen
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-wrapper {
  width: auto;
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: end;
  align-items: end;

  position: fixed;
  bottom: 6vh;
  right: -200vh;
  z-index: 99999;

  &.show {
    right: 24px;
    transition: all 0.5s ease;
  }
}

@media screen and (max-width: $mobile-lg) {
  .notification-wrapper {
    width: 100%;
    max-width: 350px;
  }
}
</style>
